import type { ComponentProps } from 'react';
import { Pressable, Text } from 'react-native';
import { TouchLock } from '../index';

import type { useLoggerContext } from './loggerContext';
import { NoteComposer } from './NoteComposer';
import { TimelineList } from './TimelineList';

type Ctx = ReturnType<typeof useLoggerContext>;

// The bottom of the timeline logger: the note composer, the single add-exercise
// button, and the reverse-chronological feed. Split out of TimelineLoggerScreen
// when LG-29's rest-freeze wraps pushed the screen over the 500-line cap (CN-27
// had already flagged it at 496 — "the next touch splits first, not during").
// Pure render shell: it reads the same context bag the screen does (mirroring
// useTimelineLogger(ctx)) plus the feed/handlers the screen derives, and owns no
// state of its own.
//
// LG-29: the add-exercise button and the feed each sit in a <TouchLock> so a
// rest freezes them too — adding an exercise or tapping a mini-card both
// navigate away from the resting set, which is a cold action (tap the timer's ✕
// first). The note composer stays LIVE on purpose: jotting a note mid-rest
// touches no set and is a genuinely useful thing to do while the clock runs.
export function TimelineFeedSection({
  ctx,
  feed,
  restFrozen,
  onSendNote,
  onDeleteNote,
  onFocusRow,
  bestE1rmForSlug,
  failFlash,
}: {
  ctx: Ctx;
  feed: ComponentProps<typeof TimelineList>['feed'];
  /** A rest is counting on the active card → freeze the navigation surfaces. */
  restFrozen: boolean;
  onSendNote: ComponentProps<typeof NoteComposer>['onSend'];
  onDeleteNote: ComponentProps<typeof TimelineList>['onDeleteNote'];
  onFocusRow: ComponentProps<typeof TimelineList>['onFocusRow'];
  bestE1rmForSlug: ComponentProps<typeof TimelineList>['bestE1rmForSlug'];
  failFlash: ComponentProps<typeof TimelineList>['flashRow'];
}) {
  const {
    draft,
    styles,
    theme,
    t,
    i18n,
    getExerciseNameBySlug,
    groupLabelFor,
    postSaveSummary,
    setPickerOpen,
  } = ctx;
  return (
    <>
      {/* Notes + feed live OUTSIDE the exercise gate — a note isn't tied to
          an exercise, so the composer is visible from the very START and
          detached from the focus card (Antenore 2026-06-04, the design
          intent: "appena prima della timeline"). Both render whether or not
          any exercise has been added. Composer stays LIVE during a rest. */}
      <NoteComposer onSend={onSendNote} t={t} theme={theme} />

      {/* Add-exercise sits BETWEEN the note composer and the timeline feed
          (inbox bdfb84a3): note form → add-exercise → timeline. Only while
          logging (postSaveSummary === null) AND once there's at least one
          exercise — the empty state already offers it via EmptyFocusCard, so
          this avoids a redundant double button. The footer suppresses its own
          copy via hideAddExercise so this is the single button. */}
      {postSaveSummary === null && draft.entries.length > 0 ? (
        // Frozen during a rest (LG-29): adding an exercise focuses it, which is
        // navigation away from the resting set — that's a cold action, do it
        // after the rest (tap ✕).
        <TouchLock locked={restFrozen}>
          <Pressable
            accessibilityRole="button"
            style={({ pressed }) => [
              styles.addExercise,
              pressed && styles.addExercisePressed,
            ]}
            onPress={() => setPickerOpen(true)}
          >
            <Text style={styles.addExerciseText}>
              {t('mobile.logger.addExercise')}
            </Text>
          </Pressable>
        </TouchLock>
      ) : null}

      {/* Chronological feed: logged sets + note mini-cards, interleaved by
          time (oldest first). Frozen during a rest (LG-29): a mini-card tap
          re-focuses that set's exercise — a cold navigation, not a mid-rest
          one. */}
      <TouchLock locked={restFrozen}>
        <TimelineList
          feed={feed}
          startedAt={draft.startedAt}
          resolveName={getExerciseNameBySlug}
          groupLabel={(groupId, kind) =>
            groupId
              ? groupLabelFor({ id: groupId, kind: kind ?? 'superset' })
              : null
          }
          onFocusRow={onFocusRow}
          onDeleteNote={onDeleteNote}
          bestE1rmForSlug={bestE1rmForSlug}
          flashRow={failFlash}
          locale={i18n.language}
          theme={theme}
        />
      </TouchLock>
    </>
  );
}
